import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HostingService} from "../hosting.service";
import {DbResponse} from "../../../../shared/interceptors/db-response";
import {Website} from "../../website";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import * as fromStore from "../../../../store";
import {combineLatest, Subscription} from 'rxjs';
import {Store} from '@ngrx/store';
import {DbDatePipe} from "../../../../shared/pipes/db-date.pipe";
import {DbTextPipe} from "../../../../shared/pipes/db-text.pipe";
import {User} from "../../../../user/user";
import {filter, first, switchMap} from 'rxjs/operators';
import {
  IFcDropdownSelectOption
} from "../../../../shared/components/form-controls/dropdown-select/fc-dropdown-select-option";
import {FormControl} from '@angular/forms';
import {Utils} from "../../../../shared/utils/utils";
import features from "./resource-usage-features.json";

@Component({
    selector: 'db-hosting-resource-usage',
    templateUrl: './hosting-resource-usage.component.html',
    styleUrls: ['./hosting-resource-usage.component.scss'],
    standalone: false
})
export class HostingResourceUsageComponent implements OnInit, OnDestroy {

  public website: Website;
  public customTooltips;
  public loading: boolean = false;
  public lineChartColors: Array<any>;
  public lineChartData: Array<any>;
  public lineChartLabels: Array<any>;
  public lineChartOptions: any;
  public lineChartLegend: boolean = false;
  public lineChartType: string = 'line';
  public subscriptions: Subscription[];
  public allVisitors: number;
  public allVisitorsByWebsite: number;
  public allVisitorsByWebsiteStartDate: any;
  public allVisitorsByWebsiteEndDate: any;
  public lineHeight: any;
  public user: User;
  public usedHostingVisits: number;
  public totalHostingVisits: number;
  public usedHostingStorage: number;
  public usedHostingStorageByWebsite: number;
  public usedHostingFileStorageByWebsite: number;
  public usedHostingDbStorageByWebsite: number;
  public totalHostingStorage: number;
  public filterDate: FormControl;
  public filterDateOptions: IFcDropdownSelectOption[];
  public prevFilterDate: any;
  public startDate: any;
  public endDate: any;
  public allDates: any[] = [];
  public canvasWrapMargin: any;
  public leftForTooltip: any;
  public firstInfoLink: string = "https://help.10web.io/hc/en-us/articles/360030131731-How-10Web-counts-visits";
  public demoUser: boolean = false;
  public features: string[] = features;

  constructor(
    private _route: ActivatedRoute,
    private _hostingService: HostingService,
    private _dialogService: DialogService,
    private _dbDatePipe: DbDatePipe,
    private _dbText: DbTextPipe,
    private _store: Store<fromStore.DbStoreState>,
  ) {
  }


  ngOnInit(): void {
    this.website = this._route.snapshot.parent.parent.data['website'];
    this.usedHostingVisits = this._route.snapshot.data['hostingVisitorsData'] ? this._route.snapshot.data['hostingVisitorsData'].visitors : null;
    this.allVisitorsByWebsite = this._route.snapshot.data['hostingVisitsByWebsiteByDate'].sum;
    this.allVisitorsByWebsiteStartDate = this._dbDatePipe.transform(new Date(this._route.snapshot.data['hostingVisitsByWebsiteByDate'].start_date), "MMM dd");
    this.allVisitorsByWebsiteEndDate = this._dbDatePipe.transform(new Date(this._route.snapshot.data['hostingVisitsByWebsiteByDate'].end_date), "MMM dd");
    this.usedHostingStorage = this._route.snapshot.data['hostingUsageData'] ? this._route.snapshot.data['hostingUsageData'].storage : null;
    this.usedHostingDbStorageByWebsite = this._route.snapshot.data['hostingUsageByWebsiteData'] ? this._route.snapshot.data['hostingUsageByWebsiteData'].db_size : 0;
    this.usedHostingFileStorageByWebsite = this._route.snapshot.data['hostingUsageByWebsiteData'] ? this._route.snapshot.data['hostingUsageByWebsiteData'].files_size : 0;

    this.filterDateOptions = [
      {
        label: this._dbText.transform('text_past7Days'),
        value: "past7"
      },
      {
        label: this._dbText.transform('text_past30Days'),
        value: "past30"
      }
    ];


    this.filterDate = new FormControl("past7");
    const startDate = new Date();
    const endDate = new Date();
    endDate.setDate(endDate.getDate() - 1);
    const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    if (this.filterDate.value === "past30") {
      startDate.setDate(startDate.getDate() - 30);
      this.lineHeight = "46px";
      if (windowWidth <= 1370) {
        this.canvasWrapMargin = "2px -11px";
      } else {
        this.canvasWrapMargin = "3px -20px";
      }
      this.leftForTooltip = 673;
    } else {
      startDate.setDate(startDate.getDate() - 7);
      this.lineHeight = "28px";
      if (windowWidth <= 1370) {
        this.canvasWrapMargin = "2px -45px";
      } else {
        this.canvasWrapMargin = "2px -85px";
      }
      this.leftForTooltip = 608;
    }


    this.startDate = startDate;
    this.endDate = endDate;
    this.addEventHandlers();


    this.lineChartColors = [
      {
        borderColor: 'rgba(41, 179, 17, 1)',
        pointRadius: 0,
        fill: false,
        pointBorderWidth: 0,
        pointHitRadius: 10,
        pointBackgroundColor: '#fff',
        pointBorderColor: 'rgba(41, 179, 17, 0.56)',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(41, 179, 17, 0.56)',
        pointHoverRadius: 5,
        pointHoverBorderWidth: 3
      }
    ];


    this.lineChartLabels = [];

    this.lineChartData = [
      {data: [], label: 'Visitors',
        borderColor: 'rgba(41, 179, 17, 1)',
        pointRadius: 0,
        fill: false,
        pointBorderWidth: 0,
        pointHitRadius: 10,
        pointBackgroundColor: '#fff',
        pointBorderColor: 'rgba(41, 179, 17, 0.56)',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: 'rgba(41, 179, 17, 0.56)',
        pointHoverRadius: 5,
        pointHoverBorderWidth: 3
      },
    ];


    const date = {
      start_date: this._dbDatePipe.transform(startDate, 'yyyy-MM-dd'),
      end_date: this._dbDatePipe.transform(endDate, 'yyyy-MM-dd')
    };
    const leftForTooltip = this.leftForTooltip;
    this.customTooltips = function (tooltip) {
      // Tooltip Element
      let tooltipEl = document.getElementById('chartjs-tooltip');
      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.setAttribute('style', "opacity: 1; position: fixed; background: rgba(23, 43, 77, 0.95); color: rgba(255, 255, 255, 0.7); border-radius: 5px; -webkit-transition: all .1s ease; transition: all .1s ease; pointer-events: none; -webkit-transform: translate(-50%, 0); transform: translate(-50%, 0); width: 100px; box-sizing: border-box; font-size: 12px; display: flex; justify-content: center");
        tooltipEl.id = 'chartjs-tooltip';
        tooltipEl.innerHTML = "<div class='tooltip-wrap'></div>";
        document.getElementById('canvas-holder').appendChild(tooltipEl);
      }

      let tooltipNew = tooltip && tooltip.tooltip ? tooltip.tooltip : null;

      // Hide if no tooltip
      if (tooltipNew.opacity === 0) {
        tooltipEl.style.opacity = "0";
        return;
      }

      // Set caret Position
      tooltipEl.classList.remove('above', 'below', 'no-transform');
      if (tooltipNew.yAlign) {
        tooltipEl.classList.add(tooltipNew.yAlign);
      } else {
        tooltipEl.classList.add('no-transform');
      }

      function getBody(bodyItem) {
        return bodyItem.lines;
      }


      // Set Text
      if (tooltipNew.body) {
        const titleLines = tooltipNew.title || [];
        const bodyLines = tooltipNew.body.map(getBody);

        let innerHtml = '<div>';

        titleLines.forEach((title) => {
          innerHtml += '<div style="text-align: left; display: inline-block; margin: 0 2px 7px;">' + title + '</div>';
        });
        innerHtml += '</div style="display: flex; align-items: center"><div>';

        bodyLines.forEach(function (body, i) {
          const colors = tooltipNew.labelColors[i];
          let style = 'background:' + colors.borderColor;
          style += ';display: inline-block';
          style += ';width: 6px';
          style += ';height: 6px';
          style += ';margin-right: 5px';
          style += ';border-radius: 5px';
          style += '; border-width: 2px';
          const value = body[0].split(':');
          const span = '<span class="chartjs-tooltip-key" style="' + style + '"></span>';
          innerHtml += '<div><div>' + span + '<span style="color: #fff; margin-right: 5px">' + value[1].replace(/\\/g, '') + '</span>' + value[0] + '</div></div>';
        });
        innerHtml += '</div>';

        const tableRoot = tooltipEl.querySelector('.tooltip-wrap');
        tableRoot.innerHTML = innerHtml;
      }

      /*const position = this._chart.canvas.getBoundingClientRect();*/

      // Display, position, and set styles for font
      tooltipEl.style.opacity = "1";
      tooltipEl.style.left = tooltipNew.caretX + leftForTooltip + 'px';
      tooltipEl.style.top = tooltipNew.caretY + 84 + 'px';
      tooltipEl.style.fontFamily = '"Open Sans", sans-serif';
      tooltipEl.style.padding = '10px';
    };


    //this._store.dispatch(new fromStore.LoadHostingVisitsByWebsiteByDate({websiteId: this.website.id, date: date}));
    this.loadHostingVisitsByWebsiteByDate({websiteId: this.website.id, date: date});
    this.prevFilterDate = this.filterDate.value;

  }

  private loadHostingVisitsByWebsiteByDate(data) {
    this._hostingService.getHostingVisitsByWebsiteByDate(data.websiteId, data.date)
      .pipe(filter((response: DbResponse): boolean => !!response),
        first())
      .subscribe((response: DbResponse): void => {

        if (response.success) {
          this.loadVisitorsChart(response);
          this.loading = false;
        }

      });
  }

  private addEventHandlers(): void {
    this.subscriptions = [];

    this.subscriptions.push(this.filterDate.valueChanges.subscribe((): void => {
      const windowWidth = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

      if (this.prevFilterDate !== this.filterDate.value) {
        this.loading = true;
        const startDate = new Date();
        const endDate = new Date();
        endDate.setDate(endDate.getDate() - 1);
        if (this.filterDate.value === "past30") {
          startDate.setDate(startDate.getDate() - 30);
          this.lineHeight = "46px";
          if (windowWidth <= 1370) {
            this.canvasWrapMargin = "2px -11px";
          } else {
            this.canvasWrapMargin = "3px -20px";
          }
          this.leftForTooltip = 673;
        } else {
          startDate.setDate(startDate.getDate() - 7);
          this.lineHeight = "28px";

          if (windowWidth <= 1370) {
            this.canvasWrapMargin = "2px -45px";
          } else {
            this.canvasWrapMargin = "2px -85px";
          }
          this.leftForTooltip = 608;
        }
        const leftForTooltip = this.leftForTooltip;
        this.customTooltips = function (tooltip) {
          // Tooltip Element
          let tooltipEl = document.getElementById('chartjs-tooltip');
          if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.setAttribute('style', "opacity: 1; position: fixed; background: rgba(23, 43, 77, 0.95); color: rgba(255, 255, 255, 0.7); border-radius: 5px; -webkit-transition: all .1s ease; transition: all .1s ease; pointer-events: none; -webkit-transform: translate(-50%, 0); transform: translate(-50%, 0); width: 100px; box-sizing: border-box; font-size: 12px; display: flex; justify-content: center");
            tooltipEl.id = 'chartjs-tooltip';
            tooltipEl.innerHTML = "<div class='tooltip-wrap'></div>";
            document.getElementById('canvas-holder').appendChild(tooltipEl);
          }

          let tooltipNew = tooltip && tooltip.tooltip ? tooltip.tooltip : null;


          // Hide if no tooltip
          if (tooltipNew.opacity === 0) {
            tooltipEl.style.opacity = "0";
            return;
          }

          // Set caret Position
          tooltipEl.classList.remove('above', 'below', 'no-transform');
          if (tooltipNew.yAlign) {
            tooltipEl.classList.add(tooltipNew.yAlign);
          } else {
            tooltipEl.classList.add('no-transform');
          }

          function getBody(bodyItem) {
            return bodyItem.lines;
          }


          // Set Text
          if (tooltipNew.body) {
            const titleLines = tooltipNew.title || [];
            const bodyLines = tooltipNew.body.map(getBody);

            let innerHtml = '<div>';

            titleLines.forEach((title) => {
              innerHtml += '<div style="text-align: left; display: inline-block; margin: 0 2px 7px;">' + title + '</div>';
            });
            innerHtml += '</div style="display: flex; align-items: center"><div>';
            bodyLines.forEach(function (body, i) {
              const colors = tooltipNew.labelColors[i];
              let style = 'background:' + colors.borderColor;
              style += ';display: inline-block';
              style += ';width: 6px';
              style += ';height: 6px';
              style += ';margin-right: 5px';
              style += ';border-radius: 5px';
              style += '; border-width: 2px';
              const value = body[0].split(':');
              const span = '<span class="chartjs-tooltip-key" style="' + style + '"></span>';
              innerHtml += '<div><div>' + span + '<span style="color: #fff; margin-right: 5px">' + value[1].replace(/\\/g, '') + '</span>' + value[0] + '</div></div>';
            });
            innerHtml += '</div>';

            const tableRoot = tooltipEl.querySelector('.tooltip-wrap');
            tableRoot.innerHTML = innerHtml;
          }

         /* const position = this._chart.canvas.getBoundingClientRect();*/

          // Display, position, and set styles for font
          tooltipEl.style.opacity = "1";
          tooltipEl.style.left = tooltipNew.caretX + leftForTooltip + 'px';
          tooltipEl.style.top = tooltipNew.caretY + 84 + 'px';
          tooltipEl.style.fontFamily = '"Open Sans", sans-serif';
          tooltipEl.style.padding = '10px';
        };
        this.startDate = startDate;
        this.endDate = endDate;
        const date = {
          start_date: this._dbDatePipe.transform(startDate, 'yyyy-MM-dd'),
          end_date: this._dbDatePipe.transform(endDate, 'yyyy-MM-dd')
        };
        this.loadHostingVisitsByWebsiteByDate({websiteId: this.website.id, date: date});

        this.prevFilterDate = this.filterDate.value;
      }
    }));

    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
        first()
      )
      .subscribe(([user, userSubscription]) => {
        this.user = user;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
        this.totalHostingVisits = this.user.hosting_services.hosting_visits.parameter_value;
        this.totalHostingStorage = this.user.hosting_services.hosting_storage.parameter_value;
      });
    if (this.user.hosting_services) {
      this._store.select(fromStore.getHostingResources)
        .subscribe((hostingResources): void => {
          if (hostingResources) {
            this.usedHostingStorage = hostingResources.storage;
          }
        });
    }


    this._store.select(fromStore.getHostingResourcesByWebsite(this.website.id)).subscribe((hostingResources: any) => {
      if (hostingResources) {
        this.usedHostingStorageByWebsite = hostingResources;
        this.usedHostingFileStorageByWebsite = hostingResources.files_size ? hostingResources.files_size : 0;
        this.usedHostingDbStorageByWebsite = hostingResources.db_size ? hostingResources.db_size : 0;
      }
    });
  }

  private loadVisitorsChart(visitors) {
    const startDate = new Date(this.startDate);
    const allDates = [];
    const formatedAllDates = [];
    while (startDate && startDate.getTime() <= this.endDate.getTime()) {
      allDates.push(this._dbDatePipe.transform(new Date(startDate), 'YYYY-MM-DD'));
      if (this.filterDate.value === "past7") {
        formatedAllDates.push(this._dbDatePipe.transform(new Date(startDate), 'MMM dd') + ', ' + Utils.getWeekName(new Date(startDate)));

      } else {
        formatedAllDates.push([this._dbDatePipe.transform(new Date(startDate), 'MMM'), this._dbDatePipe.transform(new Date(startDate), 'dd')]);

      }
      startDate.setDate(startDate.getDate() + 1);
    }
    if (visitors && visitors.data && allDates.length) {
      formatedAllDates.push("");
      formatedAllDates.unshift("");
      this.lineChartData = null;
      this.lineChartLabels = formatedAllDates;
      const _lineChartData = [
        {data: [],
          label: 'Visitors',
          borderColor: 'rgba(41, 179, 17, 1)',
          fill: false,
          pointHitRadius: 10,
          pointBackgroundColor: '#fff',
          pointBorderColor: 'rgba(41, 179, 17, 0.56)',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(41, 179, 17, 0.56)',
          pointHoverRadius: 5,
          pointHoverBorderWidth: 3
        },
      ];
      const dataFromCore = visitors.data.reduce((map: any, data: any) => {
        const date = this._dbDatePipe.transform(new Date(data.date), 'YYYY-MM-DD');
        map[date] = data.count;
        return map;
      }, {});
      this.allVisitors = 0;
      this.allDates = [];
      allDates.map(date => {
        this.allDates.push(new Date(date));
        if (dataFromCore[date]) {
          _lineChartData[0].data.push(dataFromCore[date]);
          this.allVisitors += dataFromCore[date];
        } else {
          _lineChartData[0].data.push(0);
        }
      });
      _lineChartData[0].data.unshift(0);
      _lineChartData[0].data.push(0);
      this.lineChartOptions = {
        datasets: [{
          label: 'Visitors',
          borderColor: 'rgba(41, 179, 17, 1)',
          fill: false,
          pointHitRadius: 10,
          pointBackgroundColor: '#fff',
          pointBorderColor: 'rgba(41, 179, 17, 0.56)',
          pointHoverBackgroundColor: '#fff',
          pointHoverBorderColor: 'rgba(41, 179, 17, 0.56)',
          pointHoverRadius: 5,
          pointHoverBorderWidth: 3
        }],
        plugins: {
          tooltip: {
            enabled: false, // Make sure tooltips are enabled
            mode: 'index',
            position: 'nearest', // This will place the tooltip based on the average position of the items
            intersect: false,
            external: this.customTooltips
          }
        },
        scales: {
          y: {
            ticks: {
              max: Math.max(..._lineChartData[0].data) + 1,
            },
            grid: {
              drawBorder: false,
              borderDash: [8, 4],
              color: "rgba(0, 0, 0, 0)", // Makes grid lines invisible
            },
            display: false // Hides the y-axis
          },
          x: {
            ticks: {
              stepSize: 1,
              autoSkip: false,
              maxRotation: 0,
              font: {
                size: 11 // Updated for Chart.js 3.x
              }
            },
            grid: {
              offset: true,
              borderDash: [8, 4],
              color: "rgba(0, 0, 0, 0)", // Makes grid lines invisible
            }
          },
        },
        interaction: {
          intersect: false // Updated for Chart.js 3.x, moved from hover to interaction
        },
        responsive: true,
        maintainAspectRatio: false,
      };


      setTimeout(() => {
        this.lineChartData = _lineChartData;
      }, 200);
      /*console.log(_lineChartData);*/

    }
  }


  // events
  public chartClicked(e: any): void {
    /*console.log(e);*/
  }

  public chartHovered(e: any): void {
    /*console.log(e);*/
  }


  ngOnDestroy(): void {
    this.removeEventHandlers();
  }

  private removeEventHandlers(): void {
    if (this.subscriptions) {
      this.subscriptions.forEach((subscription: Subscription): void => {
        subscription.unsubscribe();
      });
    }
    this.subscriptions = null;
  }
}
